import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

// Tab routes render inside <TabContainer>, which has its own overflow:auto and
// preserves scrollTop natively across display:none/block. ScrollToTop should
// leave them alone.
const TAB_ROUTES = new Set(['/', '/search', '/trending', '/notifications', '/profile']);

// Window-scroll cache for non-tab pages.
const scrollPositions = new Map<string, number>();

const ScrollToTop: React.FC = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const prevPathRef = useRef(location.pathname);

  // Prevent the browser from competing with our manual scroll management on web.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    // On native: PageTransition handles scroll via its own save/restore system.
    if (Capacitor.isNativePlatform()) return;

    const newPath = location.pathname;
    const prevPath = prevPathRef.current;
    if (prevPath === newPath) return;

    // Save the leaving page's window scroll only if it was a non-tab page;
    // tab pages don't scroll the window.
    if (!TAB_ROUTES.has(prevPath)) {
      scrollPositions.set(prevPath, window.scrollY);
    }
    prevPathRef.current = newPath;

    // Destination is a tab — TabContainer handles its scroll. Do nothing.
    if (TAB_ROUTES.has(newPath)) return;

    if (navType === 'POP') {
      const savedY = scrollPositions.get(newPath) ?? 0;
      window.scrollTo({ top: savedY, behavior: 'instant' });
      return;
    }

    // Non-tab PUSH (movie, watchlist, person, etc.): start at the top.
    // Use 'instant' so it happens before the browser paints — without this,
    // scroll-behavior: smooth in index.css would animate from the previous
    // page's position to 0, making every new page appear scrolled then slide up.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, navType]);

  return null;
};

export default ScrollToTop;
